import React from 'react';
import { motion } from 'framer-motion';
import ContactForm from '../components/ContactFormElevated';
import ParallaxBackground from '../components/ParallaxBackground';
import { FiPhone, FiCheckCircle } from 'react-icons/fi';

const ResidentialBuyers = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen"
    >
      {/* Hero Section with Parallax */}
      <ParallaxBackground
        image="/images/boathouse-row.jpg"
        className="py-32"
        overlay={true}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center space-x-3 bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full mb-6 border border-eagle-blue/30"
            >
              <span className="text-white text-sm font-medium tracking-wide">★ TRUSTED CLOSING SERVICES</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="font-display text-5xl md:text-6xl font-bold mb-6 text-white"
            >
              Residential Buyers
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="font-sans text-xl md:text-2xl text-eagle-sky max-w-3xl mx-auto leading-relaxed"
            >
              Professional closing services tailored specifically for homebuyers
            </motion.p>
            <motion.a
              href="tel:4845808865"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center space-x-2 mt-8 bg-gradient-to-r from-eagle-sky to-eagle-light text-white px-8 py-4 rounded-full font-semibold hover:shadow-xl transition-all shadow-lg"
            >
              <FiPhone className="h-5 w-5" />
              <span>Call Us: 484-580-8865</span>
            </motion.a>
          </motion.div>
        </div>
      </ParallaxBackground>

      {/* Services Section */}
      <section className="py-16 bg-gradient-to-b from-eagle-cream to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="relative inline-block">
              <div className="absolute -top-4 -left-4 w-24 h-24 bg-eagle-blue/20 rounded-full" />
              <h2 className="font-display text-4xl font-bold text-eagle-dark mb-4 relative">
                Residential Buyers Services
              </h2>
            </div>
            <p className="font-sans text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Purchasing a home is an exciting milestone. At Eagle Closing, we specialize in providing comprehensive closing services tailored specifically for residential buyers.
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {[
              {
                title: 'Guidance and Support',
                description: 'Buying a home can feel overwhelming, especially for first-time buyers. Our team is here to provide expert guidance and support throughout the entire process. We\'ll walk you through each step, answer your questions, and ensure you have a clear understanding of the closing process.',
              },
              {
                title: 'Contract Review',
                description: 'Our professionals meticulously review the purchase agreement, ensuring that all terms and conditions align with your expectations. We\'ll explain any legal jargon or clauses, helping you make informed decisions.',
              },
              {
                title: 'Title Examination and Insurance',
                description: 'We conduct a thorough title examination to ensure the property has a clear title, free from any liens or claims that could affect your ownership. Additionally, we assist in obtaining title insurance, providing you with protection and peace of mind regarding the property\'s ownership.',
              },
              {
                title: 'Document Preparation and Review',
                description: 'We handle the preparation and review of all closing documents, including the deed, loan documents, and closing statements. Our attention to detail ensures that all documents are accurate and compliant with legal requirements.',
              },
              {
                title: 'Coordination and Communication',
                description: 'We act as a central point of contact between all parties involved in the transaction, including real estate agents, lenders, and attorneys. We facilitate clear communication and timely exchange of information to ensure a smooth and efficient closing process.',
              },
              {
                title: 'Timely and Efficient Closings',
                description: 'We understand the importance of meeting deadlines. With our streamlined processes and commitment to efficiency, we work diligently to ensure that your closing is conducted in a timely manner, allowing you to move into your new home as planned.',
              },
            ].map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={{ y: -5, shadow: '0 20px 40px rgba(0,0,0,0.1)' }}
                className="bg-gradient-to-br from-white to-gray-50 p-6 rounded-2xl border border-gray-100 shadow-md hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-eagle-blue to-blue-600 rounded-xl flex items-center justify-center mb-4">
                  <FiCheckCircle className="h-6 w-6 text-white" />
                </div>
                <h3 className="font-display text-xl font-semibold text-eagle-dark mb-3">
                  {service.title}
                </h3>
                <p className="font-sans text-gray-600">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <p className="font-sans text-lg text-gray-700 mb-8 leading-relaxed">
              At Eagle Closing, we are committed to providing exceptional service and support to residential buyers. We strive to make the closing process as stress-free and seamless as possible, allowing you to focus on the excitement of homeownership.
            </p>
            <p className="font-sans text-lg text-gray-700 mb-8 leading-relaxed">
              Contact us today to learn more about our residential buyer services and how we can assist you in achieving a successful and hassle-free closing. Trust Eagle Closing to be your dedicated partner throughout your home buying journey, ensuring a smooth transition into your new home.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form with Parallax */}
      <ContactForm backgroundImage="/images/benjamin-franklin-bridge.jpg" />
    </motion.div>
  );
};

export default ResidentialBuyers;
